/**
 * EAA-1736: Shared media layout attributes + helpers for rich-text media nodes
 * (image, audio, embed).
 *
 * WHAT: Lets an author place any media node inline, as a block, wrapped with
 * text flowing around it (left/right), behind the text, or in front of the
 * text. "behind"/"front" are free-positioned overlays (draggable x/y offset).
 *
 * WHY (single source of truth): the exact same attribute set drives both
 * - the editor NodeViews (ResizableImageView / AudioView / EmbedView), and
 * - the static player (RichTextViewer -> generateHTML) via parseHTML/renderHTML
 *   which stamps `data-layout` / `data-x` / `data-y` onto the element so the
 *   shared CSS in tiptap.css can lay it out identically.
 * Because getRichTextExtensions() is consumed by every rich-text surface in the
 * app, these attributes are purely additive: legacy media with no layout attr
 * falls back to DEFAULT_MEDIA_LAYOUT ('block') and renders exactly as before.
 */

import type { CSSProperties } from 'react';
import type { Attributes } from '@tiptap/core';

export type MediaLayout =
    | 'inline'
    | 'block'
    | 'wrap-left'
    | 'wrap-right'
    | 'behind'
    | 'front';

export const MEDIA_LAYOUTS: readonly MediaLayout[] = [
    'inline',
    'block',
    'wrap-left',
    'wrap-right',
    'behind',
    'front',
];

/** Legacy media (no attr) renders as a block — keeps existing docs unchanged. */
export const DEFAULT_MEDIA_LAYOUT: MediaLayout = 'block';

export function isMediaLayout(value: unknown): value is MediaLayout {
    return typeof value === 'string' && (MEDIA_LAYOUTS as readonly string[]).includes(value);
}

/** Layout modes that free-position the media over the content (draggable x/y). */
export function isOverlayLayout(layout: MediaLayout): boolean {
    return layout === 'behind' || layout === 'front';
}

/** Layout modes where text flows around a floated media node. */
export function isWrapLayout(layout: MediaLayout): boolean {
    return layout === 'wrap-left' || layout === 'wrap-right';
}

function numberOrNull(raw: string | null): number | null {
    if (raw == null) return null;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
}

/**
 * Tiptap `addAttributes()` fragment shared by the image, audio, and embed nodes.
 * Spread it alongside each node's own attributes:
 *   addAttributes() { return { ...this.parent?.(), ...mediaLayoutAttributes() }; }
 *
 * `layout` renders `data-layout` only for non-default values so a legacy/block
 * node serialises identically to before (no attribute noise, no diff churn).
 */
export function mediaLayoutAttributes(): Attributes {
    return {
        layout: {
            default: DEFAULT_MEDIA_LAYOUT,
            parseHTML: (element) => {
                const value = element.getAttribute('data-layout');
                return isMediaLayout(value) ? value : DEFAULT_MEDIA_LAYOUT;
            },
            // Receives ALL node attrs, so it can also emit the overlay position
            // style for the static viewer (which has no NodeView / JS to do it).
            renderHTML: (attributes) => {
                const layout = attributes.layout as MediaLayout | undefined;
                if (!layout || layout === DEFAULT_MEDIA_LAYOUT) return {};
                const out: Record<string, string> = { 'data-layout': layout };
                if (layout === 'behind' || layout === 'front') {
                    const x = typeof attributes.x === 'number' ? attributes.x : 0;
                    const y = typeof attributes.y === 'number' ? attributes.y : 0;
                    const z = layout === 'behind' ? MEDIA_BEHIND_Z_INDEX : MEDIA_FRONT_Z_INDEX;
                    out.style = `position:absolute;left:${x}px;top:${y}px;z-index:${z};`;
                }
                return out;
            },
        },
        // x/y are the pixel offset of a behind/front overlay from its anchor.
        x: {
            default: null,
            parseHTML: (element) => numberOrNull(element.getAttribute('data-x')),
            renderHTML: (attributes) =>
                attributes.x == null ? {} : { 'data-x': String(attributes.x) },
        },
        y: {
            default: null,
            parseHTML: (element) => numberOrNull(element.getAttribute('data-y')),
            renderHTML: (attributes) =>
                attributes.y == null ? {} : { 'data-y': String(attributes.y) },
        },
    };
}

/** Values read off a media node's attrs, normalised. */
export interface MediaLayoutState {
    layout: MediaLayout;
    x: number | null;
    y: number | null;
}

export function readMediaLayoutState(attrs: Record<string, unknown>): MediaLayoutState {
    const layout = isMediaLayout(attrs.layout) ? attrs.layout : DEFAULT_MEDIA_LAYOUT;
    const x = typeof attrs.x === 'number' ? attrs.x : null;
    const y = typeof attrs.y === 'number' ? attrs.y : null;
    return { layout, x, y };
}

/** z-index applied to overlay media: behind sits under text, front sits over. */
export const MEDIA_BEHIND_Z_INDEX = -1;
export const MEDIA_FRONT_Z_INDEX = 40;

/** Wrapper class shared by every media NodeView so one CSS block styles all. */
export function mediaLayoutWrapperClass(layout: MediaLayout): string {
    return `tiptap-media tiptap-media--${layout}`;
}

type CSSDeclaration = CSSProperties;

/**
 * Inline style for a media NodeView wrapper. Only overlay modes need JS-driven
 * style (absolute position from x/y); flow modes are handled entirely in CSS.
 */
export function mediaLayoutWrapperStyle(state: MediaLayoutState): CSSDeclaration {
    if (!isOverlayLayout(state.layout)) return {};
    return {
        position: 'absolute',
        left: `${state.x ?? 0}px`,
        top: `${state.y ?? 0}px`,
        zIndex: state.layout === 'behind' ? MEDIA_BEHIND_Z_INDEX : MEDIA_FRONT_Z_INDEX,
    };
}

/**
 * Full inline style for a single media element in contexts WITHOUT the shared
 * tiptap.css (e.g. the FITB/drag-the-words/dropdown passage player, which builds
 * its own DOM). Covers every layout mode so passage media matches the editor.
 */
export function mediaLayoutInlineStyle(state: MediaLayoutState): CSSDeclaration {
    switch (state.layout) {
        case 'inline':
            return { display: 'inline-block', verticalAlign: 'middle', margin: '0 0.25rem' };
        case 'wrap-left':
            return { float: 'left', maxWidth: '50%', margin: '0.25rem 1rem 0.5rem 0' };
        case 'wrap-right':
            return { float: 'right', maxWidth: '50%', margin: '0.25rem 0 0.5rem 1rem' };
        case 'behind':
        case 'front':
            return {
                position: 'absolute',
                left: `${state.x ?? 0}px`,
                top: `${state.y ?? 0}px`,
                zIndex: state.layout === 'behind' ? MEDIA_BEHIND_Z_INDEX : MEDIA_FRONT_Z_INDEX,
                maxWidth: '60%',
            };
        default:
            // block: media are inline nodes, so force block to keep own line.
            return { display: 'block' };
    }
}
